import React, { useEffect, useMemo, useRef, useState } from "react";
import { read, utils } from "xlsx";
import { Button } from "./ui/button";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { Item, LOST, RETURN, SITE_TRANSFER } from "../pages/AddActivity";
import SearchInput from "./search-input";
import { Checkbox } from "./ui/checkbox";
import { getBackendErrorMessage, getQuantityColumnName } from "../lib/utils";
import { getInventories, getSiteInventoryItems } from "../lib/api";
import { ScrollArea } from "./ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from "./ui/table";
import { Input } from "./ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface AddItemsDrawerProps {
  selectedItems: Item[] | null;
  setSelectedItems: (data: Item[]) => void;
  setItems: (data: Item[]) => void;
  activitySubType: string;
  /**
   * Warehouse ID passed to getInventories as `warehouseId`.
   * For most subtypes this is the source warehouse; for Purchase it must be the destination
   * warehouse (receiving site), since the source is a vendor.
   */
  sourceWarehouseId?: string | null;
  sourceSiteId?: string | null;
  handleEditActivityTypeLocationAndDate: () => void;
  onClose: () => void;
}

const AddItemsDrawer: React.FC<AddItemsDrawerProps> = ({
  selectedItems,
  setSelectedItems,
  setItems,
  activitySubType,
  sourceWarehouseId,
  sourceSiteId,
  handleEditActivityTypeLocationAndDate,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const closeIntentRef = React.useRef<"close" | "edit">("close");
  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleCloseRequest = () => {
    if (hasUnsavedChanges) {
      closeIntentRef.current = "close";
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleEditClick = () => {
    if (hasUnsavedChanges) {
      closeIntentRef.current = "edit";
      setShowCloseConfirm(true);
    } else {
      handleEditActivityTypeLocationAndDate();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    const intent = closeIntentRef.current;
    setShowCloseConfirm(false);
    if (intent === "edit") {
      handleEditActivityTypeLocationAndDate();
    }
    onClose();
  };

  const usesSiteInventory =
    activitySubType === RETURN ||
    activitySubType === SITE_TRANSFER ||
    activitySubType === LOST;

  const getAllItems = async () => {
    setInventoryLoading(true);
    try {
      if (activitySubType === "Purchase" && !sourceWarehouseId) {
        setAllItems([]);
        setExpandedItems([]);
        return;
      }
      if (usesSiteInventory && !sourceSiteId) {
        setAllItems([]);
        setExpandedItems([]);
        return;
      }

      const inventoryItems =
        usesSiteInventory && sourceSiteId
          ? (await getSiteInventoryItems(sourceSiteId))
              .filter((item) => (item.totalQuantity ?? 0) > 0)
              .map((item) => ({
              id: item.inventory.id,
              item_code: item.inventory.itemCode,
              description: item.inventory.description,
              length:
                item.inventory.length === "undefined"
                  ? "---"
                  : item.inventory.length,
              quantity: item.totalQuantity,
              unit_weight: Number(item.inventory.unitWeight) || 0,
              rental_quantity: 0,
              damaged_quantity: 0,
              inventoryId: item.inventory.id,
              particulars: item.inventory.description,
            }))
          : (
              await getInventories(
                1,
                10000,
                "",
                sourceWarehouseId ?? undefined,
              )
            ).data.map((item: any) => ({
              id: item.id,
              item_code: item.itemCode,
              description: item.description,
              length: item.length === "undefined" ? "---" : item.length,
              quantity: item.availableQuantity,
              unit_weight: item.unitWeight,
              rental_quantity: 0,
              damaged_quantity: 0,
              inventoryId: item.id,
              particulars: item.description,
            }));

      setAllItems(inventoryItems);
      setExpandedItems([]);
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to fetch inventory items"),
      );
    } finally {
      setInventoryLoading(false);
    }
  };

  const toggleItemExpansion = (regionId: string) => {
    setExpandedItems((prev) =>
      prev.includes(regionId)
        ? prev.filter((id) => id !== regionId)
        : [...prev, regionId],
    );
  };

  const getItemKey = (item: Item) =>
    String(item.id ?? item.inventoryId ?? "");

  /** Serialize selection for comparison; same items + same quantities => same string. */
  const serializeSelection = (items: Item[] | null): string => {
    if (!items || items.length === 0) return "";
    return items
      .map((i) =>
        activitySubType === "Purchase" || activitySubType === "Return"
          ? `${getItemKey(i)}:${i.rental_quantity ?? 0}:${i.damaged_quantity ?? 0}`
          : `${getItemKey(i)}:${i.rental_quantity ?? 0}`,
      )
      .sort()
      .join("|");
  };

  const inventoryDrawerOpen = selectedItems != null;
  const initialSelectionRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!inventoryDrawerOpen) {
      initialSelectionRef.current = null;
      return;
    }
    initialSelectionRef.current = serializeSelection(selectedItems);
  }, [inventoryDrawerOpen]);

  const hasUnsavedChanges =
    inventoryDrawerOpen &&
    initialSelectionRef.current != null &&
    serializeSelection(selectedItems) !== initialSelectionRef.current;

  const normalizeHeader = (s: string) =>
    String(s ?? "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  /** Normalized key for Excel matching: case-insensitive description, consistent number formatting.
   * Empty length from Excel matches backend "---"; empty unit weight matches backend 0 / 0.00. */
  const getMatchKey = (
    description: string,
    length: string | number | undefined,
    unitWeight: string | number | undefined,
  ): string => {
    const d = String(description ?? "")
      .trim()
      .toLowerCase();
    const rawLen = length != null && length !== "" ? String(length).trim() : "";
    const normLen =
      rawLen === "" || /^undefined$/i.test(rawLen)
        ? "---"
        : (() => {
            const n = Number(rawLen);
            return Number.isFinite(n) ? String(n) : rawLen;
          })();
    const rawW =
      unitWeight != null && unitWeight !== "" ? String(unitWeight).trim() : "";
    const normW =
      rawW === ""
        ? "0"
        : (() => {
            const n = Number(rawW);
            return Number.isFinite(n) ? String(n) : rawW;
          })();
    return `${d}|${normLen}|${normW}`;
  };

  const parseExcelAndApplyItems = (file: File) => {
    if (!file || allItems.length === 0) return;
    setUploadingExcel(true);
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = e.target?.result;
        if (!data) return;
        const wb = read(data);
        const sheetName = wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const rawData: any[][] = utils.sheet_to_json(sheet, { header: 1 });
        const headerRowIndex = rawData.findIndex(
          (row) =>
            (row as any[]).filter(
              (cell: any) => cell !== undefined && cell !== "",
            ).length >= 2,
        );
        if (headerRowIndex === -1) {
          toast.error("Could not find a valid header row in the file.");
          return;
        }
        const header = (rawData[headerRowIndex] as any[]).map((c) =>
          normalizeHeader(String(c ?? "")),
        );
        const descIdx = header.findIndex(
          (h) =>
            /^description$|^item$|^desc$|^item\s*description$/.test(h) ||
            h === "description",
        );
        const lengthIdx = header.findIndex((h) =>
          /^length$|^length\s*\(m\)$|^length\s*\(meters\)$/i.test(h),
        );
        const weightIdx = header.findIndex((h) =>
          /^unit\s*weight$|^unit\s*weight\s*\(kg\)$|^weight$|^unit\s*weight\s*\(kg\)$/i.test(
            h,
          ),
        );
        const qtyIdx = header.findIndex((h) =>
          /^quantity$|^qty$|^rental\s*quantity$|^order\s*quantity$|^rental\s*qty$|^order\s*qty$/i.test(
            h,
          ),
        );
        if (descIdx === -1) {
          toast.error(
            "Excel must have a column for Description (or Item / Desc).",
          );
          return;
        }
        const keyToItem = new Map<string, Item>(
          allItems.map((item) => [
            getMatchKey(item.description, item.length, item.unit_weight),
            item,
          ]),
        );
        const existingKeys = new Set(
          (selectedItems ?? []).map((s) => getItemKey(s)),
        );
        let applied = 0;
        let skipped = 0;
        const nextSelected: Item[] = [...(selectedItems ?? [])];
        const dataRows = rawData.slice(headerRowIndex + 1);
        for (const row of dataRows) {
          const arr = Array.isArray(row) ? row : [];
          const desc = String(arr[descIdx] ?? "").trim();
          const len = lengthIdx >= 0 ? String(arr[lengthIdx] ?? "").trim() : "";
          const weight =
            weightIdx >= 0 ? String(arr[weightIdx] ?? "").trim() : "";
          const qtyNum =
            qtyIdx >= 0
              ? Math.max(
                  0,
                  Number(String(arr[qtyIdx] ?? "0").replace(/,/g, "")) || 0,
                )
              : 1;
          if (!desc) continue;
          const rowKey = getMatchKey(desc, len, weight);
          const match = keyToItem.get(rowKey);
          if (!match) {
            skipped += 1;
            continue;
          }
          const matchKey = getItemKey(match);
          const maxQty =
            activitySubType !== "Purchase" ? (match.quantity ?? 0) : qtyNum;
          const rentalQty = Math.min(qtyNum, maxQty);
          if (existingKeys.has(matchKey)) {
            const idx = nextSelected.findIndex(
              (s) => getItemKey(s) === matchKey,
            );
            if (idx !== -1)
              nextSelected[idx] = {
                ...nextSelected[idx],
                rental_quantity: rentalQty,
              };
            applied += 1;
          } else {
            existingKeys.add(matchKey);
            nextSelected.push({
              ...match,
              rental_quantity: rentalQty,
            });
            applied += 1;
          }
        }
        setSelectedItems(nextSelected);
        if (applied > 0 || skipped > 0)
          toast.success(
            `Applied ${applied} item(s) from Excel${skipped > 0 ? `, ${skipped} row(s) could not be matched` : ""}.`,
          );
        else toast.info("No rows could be matched to inventory items.");
      } catch (err) {
        toast.error(getBackendErrorMessage(err, "Failed to read Excel file"));
      } finally {
        setUploadingExcel(false);
        if (excelInputRef.current) excelInputRef.current.value = "";
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const displayGroups = useMemo(() => {
    const filtered = allItems.filter(
      (item) =>
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.item_code?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    const groups = filtered.reduce<Record<string, Item[]>>((acc, item) => {
      const letter =
        (item.description ?? "Other").trim().charAt(0).toUpperCase() || "Other";
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(item);
      return acc;
    }, {});
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [allItems, searchTerm]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) return;
    setExpandedItems(displayGroups.map(([prefix]) => prefix));
  }, [searchTerm, displayGroups]);

  useEffect(() => {
    if (!inventoryDrawerOpen) {
      setAllItems([]);
      setExpandedItems([]);
      setSearchTerm("");
      setInventoryLoading(false);
      return;
    }
    void getAllItems();
  }, [sourceWarehouseId, sourceSiteId, activitySubType, inventoryDrawerOpen]);

  const isPurchase = activitySubType === "Purchase";
  const isReturn = activitySubType === "Return";
  const showDamagedQty = isPurchase || isReturn;
  const quantityLabel = getQuantityColumnName(activitySubType);
  const excelDisabled =
    inventoryLoading || uploadingExcel || allItems.length === 0;
  const addItemsDisabled = inventoryLoading || selectedItems?.length === 0;

  const getSelectedItem = (itemKey: string) =>
    selectedItems?.find((selected) => getItemKey(selected) === itemKey);

  const handleItemCheckedChange = (item: Item, checked: boolean) => {
    const itemKey = getItemKey(item);
    if (checked) {
      setSelectedItems([
        ...(selectedItems ?? []),
        {
          ...item,
          rental_quantity: item.rental_quantity ?? 0,
          damaged_quantity: item.damaged_quantity ?? 0,
        },
      ]);
    } else {
      setSelectedItems(
        (selectedItems ?? []).filter(
          (selected) => getItemKey(selected) !== itemKey,
        ),
      );
    }
  };

  const handleQuantityChange = (item: Item, value: string) => {
    const itemKey = getItemKey(item);
    const raw = value.replace(/,/g, "");
    if (!/^\d*$/.test(raw)) return;
    let numValue = Number(raw) || 0;
    if (isReturn) {
      const selected = selectedItems?.find(
        (s) => getItemKey(s) === itemKey,
      );
      const damaged = selected?.damaged_quantity ?? 0;
      numValue = Math.min(numValue, Math.max(0, item.quantity - damaged));
    } else if (!isPurchase && numValue > item.quantity) {
      numValue = item.quantity;
    }
    if (numValue < 0) numValue = 0;
    setSelectedItems(
      (selectedItems ?? []).map((selected) =>
        getItemKey(selected) === itemKey
          ? { ...selected, rental_quantity: numValue }
          : selected,
      ),
    );
  };

  const handleDamagedChange = (item: Item, value: string) => {
    const itemKey = getItemKey(item);
    const raw = value.replace(/,/g, "");
    if (!/^\d*$/.test(raw)) return;
    let numValue = Math.max(0, Number(raw) || 0);
    if (isReturn) {
      const selected = selectedItems?.find(
        (s) => getItemKey(s) === itemKey,
      );
      const good = selected?.rental_quantity ?? 0;
      numValue = Math.min(numValue, Math.max(0, item.quantity - good));
    }
    setSelectedItems(
      (selectedItems ?? []).map((selected) =>
        getItemKey(selected) === itemKey
          ? { ...selected, damaged_quantity: numValue }
          : selected,
      ),
    );
  };

  const handleAddItems = () => {
    setItems(selectedItems ?? []);
    onClose();
  };

  const statusContent = inventoryLoading ? (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-gray-600">
      <Loader2 className="h-9 w-9 animate-spin text-primary-600" aria-hidden />
      <p className="text-sm font-medium text-gray-700">Loading inventory…</p>
    </div>
  ) : allItems.length === 0 ? (
    <div className="flex flex-col items-center justify-center gap-2 py-16 px-4 text-center text-gray-600">
      <p className="text-sm font-medium text-gray-800">
        {isPurchase && !sourceWarehouseId
          ? "No warehouse selected yet"
          : usesSiteInventory && !sourceSiteId
            ? "No site selected yet"
            : "No inventory items found"}
      </p>
      <p className="text-sm text-gray-500 max-w-md">
        {isPurchase && !sourceWarehouseId
          ? "Choose a destination warehouse on the previous step so inventory can load for that location."
          : usesSiteInventory && !sourceSiteId
            ? "Choose a source site on the previous step so items on site can load."
            : usesSiteInventory
              ? "This site has no stock on site, or you may not have access to it. Try another site or contact an administrator."
              : "This warehouse has no stock records, or you may not have access to them. Try another warehouse or contact an administrator."}
      </p>
    </div>
  ) : displayGroups.length === 0 ? (
    <div className="flex flex-col items-center justify-center gap-2 py-16 px-4 text-center text-gray-600">
      <p className="text-sm font-medium text-gray-800">
        No items match your search
      </p>
      <p className="text-sm text-gray-500 max-w-md">
        Try a different description, item code, or clear the search to see all
        items.
      </p>
    </div>
  ) : null;

  const mobileBody = (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="shrink-0 px-4 pt-3 pb-2">
        <SearchInput
          searchTerm={searchTerm}
          onSearchChange={(value) => setSearchTerm(value)}
          placeholder="Search items..."
          disabled={inventoryLoading}
        />
        <p className="mt-2 text-xs text-gray-500">
          Tick items, set quantities, then tap Add Items.
        </p>
      </div>
      <ScrollArea className="flex-1">
        {statusContent ?? (
          <div className="space-y-3 px-4 pb-6">
            {displayGroups.map(([prefix, items]) => {
              const expanded = expandedItems.includes(prefix);
              const selectedInGroup = items.filter((item) =>
                getSelectedItem(getItemKey(item)),
              ).length;
              return (
                <section key={prefix}>
                  <button
                    type="button"
                    onClick={() => toggleItemExpansion(prefix)}
                    className="flex h-11 w-full items-center justify-between rounded-xl bg-gray-50 px-3 text-left active:bg-gray-100"
                  >
                    <span className="text-sm font-semibold text-gray-900">
                      {prefix}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {items.length} item{items.length === 1 ? "" : "s"}
                        {selectedInGroup > 0
                          ? ` · ${selectedInGroup} selected`
                          : ""}
                      </span>
                    </span>
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                  {expanded && (
                    <div className="mt-2 space-y-2">
                      {items.map((item: Item) => {
                        const itemKey = getItemKey(item);
                        const selected = getSelectedItem(itemKey);
                        const checkboxId = `add-item-${itemKey}`;
                        return (
                          <div
                            key={itemKey}
                            className={`rounded-2xl border p-3 ${
                              selected
                                ? "border-primary-200 bg-primary-50/40"
                                : "border-gray-100 bg-white"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                id={checkboxId}
                                className="mt-0.5"
                                checked={!!selected}
                                onCheckedChange={(checked) =>
                                  handleItemCheckedChange(
                                    item,
                                    checked === true,
                                  )
                                }
                              />
                              <label
                                htmlFor={checkboxId}
                                className="min-w-0 flex-1"
                              >
                                <p className="break-words text-sm font-medium text-gray-900">
                                  {item.description || "—"}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                  Length {item.length ?? "—"} · Avail{" "}
                                  {item.quantity} · {item.unit_weight ?? "—"} kg
                                </p>
                              </label>
                            </div>
                            {selected && (
                              <div
                                className={`mt-3 grid gap-2 pl-7 ${showDamagedQty ? "grid-cols-2" : "grid-cols-1"}`}
                              >
                                <label className="space-y-1">
                                  <span className="text-xs font-medium text-gray-600">
                                    {quantityLabel}
                                  </span>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={selected.rental_quantity ?? 0}
                                    placeholder={quantityLabel}
                                    className="h-10 text-sm"
                                    onChange={(e) =>
                                      handleQuantityChange(item, e.target.value)
                                    }
                                  />
                                </label>
                                {showDamagedQty && (
                                  <label className="space-y-1">
                                    <span className="text-xs font-medium text-gray-600">
                                      Damaged Qty
                                    </span>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      value={selected.damaged_quantity ?? 0}
                                      placeholder="Damaged"
                                      className="h-10 text-sm"
                                      onChange={(e) =>
                                        handleDamagedChange(
                                          item,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </label>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <>
      <SidePanel
        open={!!selectedItems}
        onClose={handleCloseRequest}
        width="w-[900px]"
      >
        {selectedItems && (
          <div className="relative h-full flex flex-col">
            <input
              ref={excelInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) parseExcelAndApplyItems(file);
              }}
            />
            {isMobile ? (
              <PanelHeader
                title={`Select Items to ${activitySubType}`}
                onClose={handleCloseRequest}
                actions={[
                  { label: "Edit details", onClick: handleEditClick },
                  {
                    label: uploadingExcel ? "Uploading Excel…" : "Upload Excel",
                    icon: Upload,
                    disabled: excelDisabled,
                    onClick: () => excelInputRef.current?.click(),
                  },
                ]}
              />
            ) : (
              <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
                <div className="flex items-start gap-3">
                  <div className="mr-auto">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-foreground font-jakarta">
                        Select Items to {activitySubType}
                      </h2>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="w-5 h-5 cursor-pointer text-gray-600" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-white font-normal text-sm">
                              Select items with the checkbox, adjust quantities,
                              then hit Add Items to save.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleEditClick}
                        className="h-9 px-3 text-sm text-primary-600 hover:bg-primary-50"
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={excelDisabled}
                    onClick={() => excelInputRef.current?.click()}
                    className="text-nowrap"
                  >
                    <Upload className="h-4 w-4 mr-1.5" />
                    Upload Excel
                  </Button>
                  <Button
                    disabled={addItemsDisabled}
                    onClick={handleAddItems}
                    className="text-nowrap"
                  >
                    Add Items
                  </Button>
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={handleCloseRequest}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {isMobile ? (
              mobileBody
            ) : (
              <div className="flex-grow overflow-hidden px-6">
                <SearchInput
                  searchTerm={searchTerm}
                  onSearchChange={(value) => setSearchTerm(value)}
                  placeholder="Search items..."
                  className=" mb-4"
                  disabled={inventoryLoading}
                />
                <ScrollArea className="h-full min-h-[280px]">
                  {statusContent ?? (
                    <div className="mb-32">
                      <Table>
                        <TableHeader className="sticky top-0 bg-white z-10">
                          <TableHeaderRow>
                            <TableColumnHeader className="w-12">
                              {" "}
                            </TableColumnHeader>
                            <TableColumnHeader>Select</TableColumnHeader>
                            <TableColumnHeader>Description</TableColumnHeader>
                            <TableColumnHeader>Length</TableColumnHeader>
                            <TableColumnHeader>Available Qty</TableColumnHeader>
                            <TableColumnHeader>
                              {getQuantityColumnName(activitySubType)}
                            </TableColumnHeader>
                            {showDamagedQty && (
                              <TableColumnHeader>Damaged Qty</TableColumnHeader>
                            )}
                            <TableColumnHeader>
                              Unit Weight(KG)
                            </TableColumnHeader>
                          </TableHeaderRow>
                        </TableHeader>

                        <TableBody>
                          {displayGroups.map(([prefix, items]) => (
                            <React.Fragment key={prefix}>
                              <TableRow
                                key={prefix + "-header"}
                                className="font-semibold text-lg cursor-pointer"
                                onClick={() => toggleItemExpansion(prefix)}
                              >
                                <TableCell>
                                  {expandedItems.includes(prefix) ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </TableCell>
                                <TableCell
                                  colSpan={
                                    showDamagedQty ? 7 : 6
                                  }
                                >
                                  <p className="text-sm">{prefix}</p>
                                </TableCell>
                              </TableRow>

                              {/* Child Rows */}
                              {expandedItems.includes(prefix) &&
                                items.map((item: Item) => {
                                  const itemKey = getItemKey(item);
                                  const selected = getSelectedItem(itemKey);

                                  return (
                                    <TableRow
                                      key={itemKey}
                                      className="border-t"
                                    >
                                      <TableCell></TableCell>
                                      <TableCell>
                                        <Checkbox
                                          checked={!!selected}
                                          onCheckedChange={(checked) =>
                                            handleItemCheckedChange(
                                              item,
                                              !!checked,
                                            )
                                          }
                                        />
                                      </TableCell>
                                      <TableCell className="pl-6 max-w-[220px]">
                                        <span title={item.description}>
                                          {item.description || "—"}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        {item.length ?? "—"}
                                      </TableCell>
                                      <TableCell>{item.quantity}</TableCell>
                                      <TableCell>
                                        <Input
                                          type="text"
                                          value={
                                            selected?.rental_quantity ??
                                            item.rental_quantity ??
                                            0
                                          }
                                          placeholder={quantityLabel}
                                          className="text-sm min-w-16"
                                          onChange={(e) =>
                                            handleQuantityChange(
                                              item,
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </TableCell>
                                      {showDamagedQty && (
                                        <TableCell>
                                          <Input
                                            type="text"
                                            value={
                                              selected?.damaged_quantity ??
                                              item.damaged_quantity ??
                                              0
                                            }
                                            placeholder="Damaged"
                                            className="text-sm min-w-16"
                                            onChange={(e) =>
                                              handleDamagedChange(
                                                item,
                                                e.target.value,
                                              )
                                            }
                                          />
                                        </TableCell>
                                      )}
                                      <TableCell className="pl-6">
                                        {item.unit_weight ?? "—"}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
            {isMobile && (
              <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3">
                <Button
                  disabled={addItemsDisabled}
                  className="h-12 w-full rounded-2xl text-[15px] font-semibold"
                  onClick={handleAddItems}
                >
                  {selectedItems.length > 0
                    ? `Add ${selectedItems.length} item${selectedItems.length === 1 ? "" : "s"}`
                    : "Add Items"}
                </Button>
              </div>
            )}
          </div>
        )}
      </SidePanel>

      {selectedItems && (
        <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
          <DialogContent
            className="sm:max-w-md"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>Unsaved selections</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-600">
              You have selected items that have not been added yet. Closing will
              discard your selections. Do you want to continue?
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowCloseConfirm(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmClose}>
                Discard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AddItemsDrawer;
